/**
 * Task Comments Component
 *
 * Collapsible section for viewing and adding comments to a task.
 * Loads comments immediately on mount to show count badge.
 * AI comments (authorType: 'ai') are visually distinct from user comments.
 * Posting a comment with @danny starts polling for an AI response.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Loader2, Send, Paperclip, AlertCircle, Pencil, X, Check } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import clsx from 'clsx';
import type { Comment } from '../types';
import { getTaskComments, addTaskComment, updateTaskComment } from '../api/client';

interface TaskCommentsProps {
  taskId: string;
  /** Whether the task is completed (disables adding comments) */
  isCompleted?: boolean;
}

const DANNY_MENTION_RE = /@danny/i;
const SOFT_LIMIT_BYTES = 3 * 1024; // 3KB warning threshold
const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 120_000;

export function TaskComments({ taskId, isCompleted = false }: TaskCommentsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [comments, setComments] = useState<Comment[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New comment state
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Polling state (for @danny AI response)
  const [isPolling, setIsPolling] = useState(false);
  const pollingStartRef = useRef<Date | null>(null);

  // Load comments immediately when taskId changes
  useEffect(() => {
    let cancelled = false;

    const loadComments = async () => {
      setIsLoading(true);
      setError(null);
      setIsExpanded(false);
      setNewComment('');
      setEditingId(null);
      setIsPolling(false);

      try {
        const result = await getTaskComments(taskId);
        if (!cancelled) {
          setComments(result?.data ?? []);
          setHasLoaded(true);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          console.error('Failed to load comments:', err);
          setError(err instanceof Error ? err.message : 'Failed to load comments');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadComments();
    return () => { cancelled = true; };
  }, [taskId]);

  // Polling effect — fires after @danny comment, stops when AI reply appears
  const refreshComments = useCallback(async () => {
    try {
      const result = await getTaskComments(taskId);
      const fresh = result?.data ?? [];
      setComments(fresh);

      const pollingStart = pollingStartRef.current;
      if (pollingStart) {
        const hasAiResponse = fresh.some(
          (c) => c.authorType === 'ai' && new Date(c.createdAt) > pollingStart,
        );
        if (hasAiResponse) {
          setIsPolling(false);
        }
      }
    } catch {
      // Silently ignore poll errors
    }
  }, [taskId]);

  // Refresh when Danny (chat assistant) attaches a comment to this task.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ taskId?: string }>).detail;
      if (detail?.taskId === taskId) {
        refreshComments();
      }
    };
    window.addEventListener('danny-comments-updated', handler);
    return () => window.removeEventListener('danny-comments-updated', handler);
  }, [taskId, refreshComments]);

  useEffect(() => {
    if (!isPolling) return;

    const interval = setInterval(refreshComments, POLL_INTERVAL_MS);
    const timeout = setTimeout(() => {
      setIsPolling(false);
      clearInterval(interval);
    }, POLL_TIMEOUT_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isPolling, refreshComments]);

  const handleToggle = () => setIsExpanded((v) => !v);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const comment = await addTaskComment(taskId, newComment.trim());
      setComments((prev) => [...prev, comment]);

      if (DANNY_MENTION_RE.test(newComment)) {
        pollingStartRef.current = new Date();
        setIsPolling(true);
      }

      setNewComment('');
      inputRef.current?.focus();
    } catch (err: unknown) {
      console.error('Failed to add comment:', err);
      setError(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const startEdit = (comment: Comment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const saveEdit = async (commentId: string) => {
    if (!editContent.trim() || isSavingEdit) return;
    setIsSavingEdit(true);
    try {
      const original = comments.find((c) => c.id === commentId);
      const updated = await updateTaskComment(taskId, commentId, editContent.trim());
      setComments((prev) => prev.map((c) => (c.id === commentId ? updated : c)));
      setEditingId(null);
      setEditContent('');

      const hadMention = original && DANNY_MENTION_RE.test(original.content);
      if (!hadMention && DANNY_MENTION_RE.test(editContent)) {
        pollingStartRef.current = new Date();
        setIsPolling(true);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update comment');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const contentBytes = new TextEncoder().encode(newComment).length;

  return (
    <div className="space-y-2.5">
      {/* Header */}
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between text-left cursor-pointer group -mx-2 px-2 py-2 rounded-lg hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark/50 transition-colors"
      >
        <h4 className="text-sm md:text-[13px] font-medium text-text-tertiary-light dark:text-text-tertiary-dark flex items-center gap-2">
          <span>Comments</span>
          {isLoading ? (
            <Loader2 className="w-3 h-3 animate-spin text-text-tertiary-light" />
          ) : comments.length > 0 ? (
            <span className="text-text-tertiary-light dark:text-text-tertiary-dark font-normal">
              ({comments.length})
            </span>
          ) : null}
        </h4>
        <ChevronDown
          className={clsx(
            'w-4 h-4 text-text-tertiary-light dark:text-text-tertiary-dark transition-transform',
            isExpanded && 'rotate-180',
          )}
        />
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="space-y-2">
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 rounded">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {!isLoading && hasLoaded && (
            <>
              {comments.length === 0 ? (
                <p className="text-[13px] text-text-tertiary-light dark:text-text-tertiary-dark py-2 text-center">
                  No comments yet
                </p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {comments.map((comment) => (
                    <div
                      key={comment.id}
                      className={clsx(
                        'py-[11px] px-[13px] rounded-lg',
                        comment.authorType === 'ai'
                          ? 'bg-blue-50 dark:bg-blue-950/30 border-l-[3px] border-blue-400 dark:border-blue-500'
                          : 'bg-surface-base-light dark:bg-surface-hover-dark/50',
                      )}
                    >
                      {/* Author + timestamp row */}
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className={clsx(
                            'text-xs font-semibold',
                            comment.authorType === 'ai'
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-text-tertiary-light dark:text-text-tertiary-dark',
                          )}
                        >
                          {comment.authorType === 'ai' ? 'Danny' : 'You'}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs md:text-[11px] text-text-tertiary-light dark:text-text-tertiary-dark">
                            {comment.createdAt ?? comment.postedAt
                              ? formatDistanceToNow(
                                  parseISO((comment.createdAt ?? comment.postedAt)!),
                                  { addSuffix: true },
                                )
                              : 'just now'}
                            {comment.updatedAt && ' (edited)'}
                          </span>
                          {comment.authorType === 'user' && !isCompleted && editingId !== comment.id && (
                            <button
                              onClick={() => startEdit(comment)}
                              className="p-0.5 rounded text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors"
                              title="Edit comment"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Content or edit form */}
                      {editingId === comment.id ? (
                        <div className="space-y-1.5">
                          <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            rows={3}
                            className={clsx(
                              'w-full py-2 px-3 text-sm rounded border resize-none',
                              'bg-white dark:bg-surface-hover-dark border-border-subtle-light dark:border-border-subtle-dark',
                              'text-text-primary-light dark:text-text-primary-dark',
                              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                            )}
                          />
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => saveEdit(comment.id)}
                              disabled={!editContent.trim() || isSavingEdit}
                              className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-50"
                            >
                              {isSavingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              Save
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-border-subtle-light dark:border-border-subtle-dark text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark/50"
                            >
                              <X className="w-3 h-3" />
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm md:text-[13px] text-text-secondary-light dark:text-text-primary-dark whitespace-pre-wrap leading-relaxed">
                            {comment.content}
                          </p>
                          {comment.attachment && (
                            <a
                              href={comment.attachment.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              <Paperclip className="w-3 h-3" />
                              {comment.attachment.fileName}
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  ))}

                  {/* "Danny is thinking..." indicator */}
                  {isPolling && (
                    <div className="py-[11px] px-[13px] rounded-lg bg-blue-50 dark:bg-blue-950/30 border-l-[3px] border-blue-400 dark:border-blue-500">
                      <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">Danny</span>
                      <p className="text-sm md:text-[13px] text-blue-500 dark:text-blue-400 italic mt-1 flex items-center gap-1.5">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Danny is thinking…
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Add comment form */}
              {!isCompleted && (
                <form onSubmit={handleSubmit} className="mt-1">
                  <div className="flex gap-2">
                    <textarea
                      ref={inputRef}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Add a comment… (mention @danny for AI help)"
                      rows={2}
                      disabled={isSubmitting}
                      className={clsx(
                        'flex-1 py-[11px] px-[13px] text-base md:text-[13px] rounded-lg resize-none',
                        'bg-surface-base-light dark:bg-surface-hover-dark/50 border border-border-subtle-light dark:border-border-subtle-dark',
                        'text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-secondary-dark',
                        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                        'disabled:opacity-50',
                      )}
                    />
                    <button
                      type="submit"
                      disabled={!newComment.trim() || isSubmitting}
                      className={clsx(
                        'px-3 py-2 rounded-lg transition-colors self-end',
                        'bg-primary-700 text-white hover:bg-primary-800',
                        'disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer',
                        'flex items-center justify-center',
                      )}
                      title="Add comment (Cmd+Enter)"
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <p className="text-xs md:text-[11px] text-text-tertiary-light dark:text-text-tertiary-dark">
                      Press Cmd+Enter to submit
                    </p>
                    {contentBytes > SOFT_LIMIT_BYTES && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        Comment is long ({Math.round(contentBytes / 1024)}KB) — consider trimming
                      </p>
                    )}
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
